import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Chip
} from '@mui/material';
import {
  LocationOn,
  CheckCircle,
  Error as ErrorIcon,
  MyLocation,
  HowToReg,
  Login,
  Logout,
  HourglassEmpty
} from '@mui/icons-material';
import { FaceCapture } from './FaceCapture';
import { attendanceApi, AttendanceStatus } from '../../services/attendanceService';
import { getDistance } from 'geolib';

interface AttendanceCheckInProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

type AttendanceStep = 'status' | 'location' | 'face' | 'complete' | 'checkout-face';

export function AttendanceCheckIn({ onSuccess, onError }: AttendanceCheckInProps) {
  const [status, setStatus] = useState<AttendanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<AttendanceStep>('status');
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load initial status
  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await attendanceApi.getTodayStatus();
      setStatus(data);

      // Determine which step to show
      if (data.isComplete) {
        setCurrentStep('complete');
      } else if (data.isCheckedIn) {
        setCurrentStep('complete'); // Show checkout option
      } else if (!data.faceRegistered) {
        setCurrentStep('face'); // Need to register face first
      } else {
        setCurrentStep('location');
      }
    } catch (err) {
      const errorMsg = (err as Error).message || 'Failed to load attendance status';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = useCallback(async () => {
    setLocationLoading(true);
    setLocationError(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      };

      setLocation(coords);

      // Check if within geofence
      if (status?.vendorLocation?.configured && status.vendorLocation.latitude && status.vendorLocation.longitude) {
        const distance = getDistance(
          { latitude: coords.latitude, longitude: coords.longitude },
          { latitude: status.vendorLocation.latitude, longitude: status.vendorLocation.longitude }
        );

        if (distance > status.vendorLocation.radiusMeters) {
          setLocationError(
            `You are ${Math.round(distance)}m away from the venue. Please move within ${status.vendorLocation.radiusMeters}m to check in.`
          );
        } else {
          setCurrentStep('face');
        }
      } else {
        // No geofence configured, proceed
        setCurrentStep('face');
      }
    } catch (err) {
      const geoError = err as GeolocationPositionError;
      let errorMsg = 'Failed to get location';
      if (geoError.code === 1) {
        errorMsg = 'Location permission denied. Please allow location access.';
      } else if (geoError.code === 2) {
        errorMsg = 'Location unavailable. Please check your GPS settings.';
      } else if (geoError.code === 3) {
        errorMsg = 'Location request timed out. Please try again.';
      }
      setLocationError(errorMsg);
    } finally {
      setLocationLoading(false);
    }
  }, [status]);

  const handleFaceCapture = useCallback(async (embedding: number[]) => {
    if (!location) {
      setError('Location not available');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (!status?.faceRegistered) {
        // Register face first
        await attendanceApi.registerFace(embedding);
      }

      // Check in
      const result = await attendanceApi.checkIn(embedding, location.latitude, location.longitude);
      setSuccessMessage(`Checked in successfully! Confidence: ${(result.faceMatchConfidence * 100).toFixed(1)}%`);
      setCurrentStep('complete');
      onSuccess?.();

      // Reload status
      await loadStatus();
    } catch (err) {
      const errorMsg = (err as Error).message || 'Failed to check in';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }, [location, status, onSuccess, onError]);

  // Initiate checkout flow - start with face capture
  const handleCheckOutStart = useCallback(() => {
    setCurrentStep('checkout-face');
  }, []);

  // Complete checkout with face embedding
  const handleCheckOutFaceCapture = useCallback(async (embedding: number[]) => {
    setSubmitting(true);
    setError(null);

    try {
      // Get current location for checkout
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const result = await attendanceApi.checkOut(
        embedding,
        position.coords.latitude,
        position.coords.longitude
      );

      // Check if checkout needs manual approval
      if (result.pendingApproval) {
        // Build a user-friendly message
        let issueDesc = '';
        if (result.verificationIssues && result.verificationIssues.length > 0) {
          const issues: string[] = [];
          for (const issue of result.verificationIssues) {
            if (issue.startsWith('CHECKOUT_FACE_MISMATCH')) {
              issues.push('face verification');
            } else if (issue.startsWith('CHECKOUT_OUTSIDE_GEOFENCE')) {
              const match = issue.match(/:(\d+)m/);
              issues.push(match ? `location (${match[1]}m away)` : 'location');
            } else if (issue === 'CHECKOUT_FACE_NOT_PROVIDED') {
              issues.push('face not captured');
            }
          }
          if (issues.length > 0) {
            issueDesc = ` Issue: ${issues.join(', ')}.`;
          }
        }
        setSuccessMessage(`Checkout recorded pending approval.${issueDesc} Working time: ${result.workingHours}`);
      } else {
        const confidenceMsg = result.faceMatchConfidence
          ? ` Face match: ${(result.faceMatchConfidence * 100).toFixed(1)}%`
          : '';
        setSuccessMessage(`Checked out successfully! Working time: ${result.workingHours}${confidenceMsg}`);
      }
      setCurrentStep('complete');
      onSuccess?.();

      // Reload status
      await loadStatus();
    } catch (err) {
      const errorMsg = (err as Error).message || 'Failed to check out';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }, [onSuccess, onError]);

  const handleFaceRegistration = useCallback(async (embedding: number[]) => {
    setSubmitting(true);
    setError(null);

    try {
      await attendanceApi.registerFace(embedding);
      setSuccessMessage('Face registered successfully!');

      // Continue to location step
      setCurrentStep('location');

      // Reload status
      await loadStatus();
    } catch (err) {
      const errorMsg = (err as Error).message || 'Failed to register face';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }, [onError]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading attendance status...
        </Typography>
      </Box>
    );
  }

  const steps = ['Location', 'Face', 'Done'];
  const activeStep = currentStep === 'location' ? 0 : currentStep === 'face' ? 1 : 2;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status Display */}
      {status && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Today's Attendance
            </Typography>
            <Chip
              label={
                status.pendingApproval ? 'Pending Approval' :
                status.isCheckedIn ? 'Checked In' :
                status.isComplete ? 'Completed' : 'Not Checked In'
              }
              color={
                status.pendingApproval ? 'warning' :
                status.isCheckedIn ? 'success' :
                status.isComplete ? 'default' : 'warning'
              }
              size="small"
            />
          </Box>
          {status.checkInTime && (
            <Typography variant="body2">
              Check-in: {new Date(status.checkInTime).toLocaleTimeString()}
            </Typography>
          )}
          {status.checkOutTime && (
            <Typography variant="body2">
              Check-out: {new Date(status.checkOutTime).toLocaleTimeString()}
            </Typography>
          )}
          {status.workingHours && (
            <Typography variant="body2">
              Working time: {status.workingHours}
            </Typography>
          )}
        </Paper>
      )}

      {/* Already checked in - show checkout option */}
      {status?.isCheckedIn && !status.isComplete && currentStep !== 'checkout-face' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            You're on duty!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            You checked in at {status.checkInTime ? new Date(status.checkInTime).toLocaleTimeString() : 'today'}.
            Click below when you're done for the day.
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<Logout />}
            onClick={handleCheckOutStart}
            disabled={submitting}
          >
            Check Out
          </Button>
        </Box>
      )}

      {/* Checkout face verification step */}
      {status?.isCheckedIn && !status.isComplete && currentStep === 'checkout-face' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Logout color="error" sx={{ mr: 1 }} />
            <Typography variant="h6">Verify Face for Check Out</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Look at the camera to verify your identity before checking out.
          </Typography>
          {submitting ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Verifying and checking out...
              </Typography>
            </Box>
          ) : (
            <>
              <FaceCapture
                onCapture={handleCheckOutFaceCapture}
                onError={(err) => setError(err)}
                captureButtonText="Verify & Check Out"
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="text"
                  onClick={() => setCurrentStep('complete')}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Completed attendance */}
      {status?.isComplete && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          {status.pendingApproval ? (
            <>
              <HourglassEmpty color="warning" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Checkout Pending Approval
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
                Your checkout has been recorded but needs manager approval.
                <br />
                Working time: {status.workingHours}
              </Typography>
              {status.flags && status.flags.length > 0 && (
                <Alert severity="warning" sx={{ maxWidth: 400, width: '100%' }}>
                  {status.flags.some(f => f.includes('FACE_MISMATCH')) && 'Face verification did not match. '}
                  {status.flags.some(f => f.includes('OUTSIDE_GEOFENCE')) && 'Location was outside venue area. '}
                  {status.flags.some(f => f === 'CHECKOUT_FACE_NOT_PROVIDED') && 'Face was not captured. '}
                </Alert>
              )}
            </>
          ) : (
            <>
              <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Attendance Complete!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Great work today! You worked {status.workingHours}.
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Check-in flow */}
      {!status?.isCheckedIn && !status?.isComplete && (
        <>
          {/* Stepper */}
          {status?.faceRegistered && (
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.75rem', whiteSpace: 'nowrap' } }}>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {/* Face Registration Step (first time only) */}
          {!status?.faceRegistered && currentStep === 'face' && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HowToReg color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Register Your Face</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This is a one-time setup. Your face will be used to verify your identity during check-in.
              </Typography>
              <FaceCapture
                onCapture={handleFaceRegistration}
                onError={(err) => setError(err)}
                captureButtonText="Register Face"
              />
            </Box>
          )}

          {/* Location Step */}
          {status?.faceRegistered && currentStep === 'location' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              <LocationOn color="primary" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Verify Your Location
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                We need to verify you're at the venue before checking in.
              </Typography>

              {locationError && (
                <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 400 }}>
                  {locationError}
                </Alert>
              )}

              {location && !locationError && status?.vendorLocation?.configured && (
                <Alert severity="success" sx={{ mb: 2, width: '100%', maxWidth: 400 }}>
                  Location verified! You're within the venue area.
                </Alert>
              )}

              <Button
                variant="contained"
                size="large"
                startIcon={locationLoading ? <CircularProgress size={20} color="inherit" /> : <MyLocation />}
                onClick={getLocation}
                disabled={locationLoading}
              >
                {locationLoading ? 'Getting Location...' : 'Verify Location'}
              </Button>

              {!status?.vendorLocation?.configured && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                  Note: Venue location not configured. Location will be recorded but not verified.
                </Typography>
              )}
            </Box>
          )}

          {/* Face Verification Step */}
          {status?.faceRegistered && currentStep === 'face' && location && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Login color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Verify Your Face</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Look at the camera to verify your identity and check in.
              </Typography>
              {submitting ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Verifying and checking in...
                  </Typography>
                </Box>
              ) : (
                <FaceCapture
                  onCapture={handleFaceCapture}
                  onError={(err) => setError(err)}
                  captureButtonText="Check In"
                />
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
